/**
 * Social Media Integrations API Service
 * Handles OAuth flows and social media operations for YouTube, X, and Gmail
 */

import { BACKEND_CONFIG } from '../config/backendConfig';

// Configuration
const INTEGRATIONS_CONFIG = {
  // Same-origin integrations base, derived from the API host (which is
  // same-origin in prod via the nginx /integrations proxy). Hardcoding
  // apsrecruiting.io made these cross-site requests that strict browsers
  // (Edge/Safari tracking prevention) blocked — the email composer hung on
  // "Initializing...". Mirrors socketService's BACKEND_CONFIG-derived host.
  BASE_URL: `${BACKEND_CONFIG.baseURL.replace('/api', '')}/integrations/v1`,

  API_KEY: '745fb8c96a03838fa38cd7e3ca92966d1dcb7ce293665b02b74c77e78c23bf2a',

  // Enable integrations for real usage
  ENABLED: true,
};

// The integrations API checks any user_id on the request against this session.
function integrationsHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'APS-API-Key': INTEGRATIONS_CONFIG.API_KEY,
  };
  const sessionToken = sessionStorage.getItem('sessionToken') || localStorage.getItem('sessionToken');
  if (sessionToken) {
    headers.Authorization = `Bearer ${sessionToken}`;
  }
  return headers;
}

export interface AuthRecord {
  user_id: string;
  provider: 'youtube' | 'x' | 'gmail'; // Updated to match API spec
  provider_user_id: string;
  access_token: string;
  refresh_token?: string;
  expires_at: string;
  scope: string;
  created_at: string;
  updated_at: string;
}

export interface AuthUrlResponse {
  authUrl: string;
  state?: string;
  codeVerifier?: string; // Required for X OAuth flow
}

export interface ConnectionStatus {
  youtube: boolean;
  twitter: boolean;
  gmail: boolean;
}

export interface CoachTweet {
  id: number;
  coach_id: number;
  school_id: number;
  tweet_id: string;
  author_username: string;
  author_name: string;
  text: string;
  created_at: string;
  lang: string;
  retweet_count: number;
  reply_count: number;
  like_count: number;
  quote_count: number;
  bookmark_count: number;
  impression_count: number;
}

export interface CoachTweetsResponse {
  success: boolean;
  data: {
    coach_id: number;
    tweets: CoachTweet[];
    count: number;
  };
}

export interface XDMMessage {
  id: string;
  dm_event_id: string;
  conversation_id: string;
  event_type: string;
  sender_id: string;
  text: string;
  is_sent_by_user: boolean;
  event_created_at: string;
  created_at: string;
  attachments: any;
  participant_ids: string[];
  coach_ids: string[];
  school_ids: string[];
}

export interface XDMConversationResponse {
  success: boolean;
  data: {
    conversation_id: string | null;
    messages: XDMMessage[];
  };
}

export interface XDMSendResponse {
  success: boolean;
  message: string;
  data: {
    dm_conversation_id: string;
    dm_event_id: string;
    event: any;
    stored_dm: any;
    stored_event: any;
  };
}

export interface XDMThread {
  dm_conversation_id: string;
  participant_ids: string[];
  coach_ids: string[];
  school_ids: string[];
  created_at: string;
  updated_at: string;
  messages: XDMMessage[];
}

export interface XDMThreadsResponse {
  success: boolean;
  data: {
    user_id: string;
    thread_count: number;
    message_count: number;
    threads: XDMThread[];
  };
}

export interface CanSendDMResponse {
  success: boolean;
  message: string;
  data: {
    user_id: string;
    target_handle: string;
    can_send_dm: boolean;
    // 'needs_reauth' = the user's X token lacks the dm scope (reconnect);
    // 'dm_closed' = the coach doesn't accept DMs. Absent when can_send_dm is true.
    reason?: 'needs_reauth' | 'dm_closed';
  };
}


/** A Gmail send that did not go out, with the integration service's reason. */
export class GmailSendError extends Error {
  code: string | null;
  reauthReason: string | null;
  /** HTTP status of the answer, when there was one. */
  status: number | null;
  constructor(message: string, code: string | null, reauthReason: string | null, status: number | null = null) {
    super(message);
    this.name = 'GmailSendError';
    this.code = code;
    this.reauthReason = reauthReason;
    this.status = status;
  }
}

/**
 * True when the server answered a send-bulk call and made no job, so the next
 * Send can use a new client_request_id. A network error or a 5xx may have
 * made one, so the retry keeps the old id and gets that job back.
 */
export function isGmailBulkRejected(error: unknown): boolean {
  const status = (error as { status?: number | null } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/** What to tell the user when a coach email did not send. */
export function gmailSendErrorMessage(error: unknown): string {
  const e = error as { code?: string | null; message?: string } | null;
  if (e?.code === 'reauth_required') {
    return 'Gmail needs permission to send email. Reconnect Gmail on the Messages page and allow every permission.';
  }
  if (e?.code === 'free_limit_reached' && e.message) return e.message;
  if (e?.code === 'payload_too_large') {
    return 'This email is too large to send to this many coaches. Send it to fewer coaches at a time.';
  }
  // The server says what is wrong with the request. Trying again would fail the same way.
  if (e?.code === 'invalid_request' && e.message) return e.message;
  if (e?.code === 'forbidden' || (error as { status?: number | null } | null)?.status === 403) {
    return 'Your session does not match this account. Sign out and sign in again, then send.';
  }
  return 'Failed to send email. Please try again.';
}

/** True when every later send would fail the same way, so a bulk send should stop. */
export function isGmailReconnectError(error: unknown): boolean {
  return (error as { code?: string | null } | null)?.code === 'reauth_required';
}

/** Most messages one send-bulk call takes. */
export const GMAIL_BULK_MAX_MESSAGES = 1000;

/** One coach's email in a send-bulk call, already personalized. */
export interface GmailBulkMessage {
  email: string;
  coach_id?: string;
  school_id?: string;
  subject: string;
  body: string;
}

export interface GmailAttachment {
  filename: string;
  url: string;
  content_type: string;
  size?: number;
}

export interface GmailBulkSendRequest {
  messages: GmailBulkMessage[];
  cc_emails?: string[];
  bcc_emails?: string[];
  attachments?: GmailAttachment[];
  display_name?: string;
  client_request_id?: string;
}

/** The server took the list. It sends the emails and tells the sender when it is done. */
export interface GmailBulkSendResult {
  job_id: string;
  total: number;
  queued: number;
  free_limit_blocked: number;
}

/** A new id for one Send click. A repeat POST with it gets the same job back. */
export function newClientRequestId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The client_request_id a composer holds, with the payload it was made for. */
export interface ClientRequestIdSlot {
  id: string;
  key: string;
}

/**
 * The client_request_id for this payload. A retry of the same payload keeps
 * the id, so a job the first try made comes back and nothing sends twice. A
 * changed payload gets a new id, so an edit is never swapped for the old job.
 * key is any string that changes when the payload changes.
 */
export function clientRequestIdFor(slot: { current: ClientRequestIdSlot | null }, key: string): string {
  if (slot.current && slot.current.key === key) return slot.current.id;
  const id = newClientRequestId();
  slot.current = { id, key };
  return id;
}

class IntegrationsAPI {
  /**
   * Check if integrations are available
   */
  private isAvailable(): boolean {
    return INTEGRATIONS_CONFIG.ENABLED;
  }

  /**
   * Generate OAuth authorization URL for a platform
   */
  async getAuthUrl(platform: 'youtube' | 'x' | 'gmail', athleteId: string): Promise<AuthUrlResponse> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    try {
      let endpoint: string;
      
      switch (platform) {
        case 'youtube':
          endpoint = `/youtube/auth-url?state=${athleteId}&client=web`;
          break;
        case 'x':
          endpoint = `/auth-url?integration_type=x&user_id=${athleteId}&client=web`;
          break;
        case 'gmail':
          endpoint = `/gmail/auth-url?user_id=${athleteId}&client=web`;
          break;
        default:
          throw new Error(`Unsupported platform: ${platform}`);
      }

      const fullUrl = `${INTEGRATIONS_CONFIG.BASE_URL}${endpoint}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

            
      if (!response.ok) {
        const errorText = await response.text();
                
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          // Handle standardized API error format
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        
        throw new Error(errorData.error || `Failed to get auth URL for ${platform}`);
      }

      const responseText = await response.text();
            
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
                throw new Error('Invalid JSON response from integrations service');
      }

      // Check API success status
      if (responseData.success === false) {
        throw new Error(responseData.error || `Failed to get auth URL for ${platform}`);
      }

      // Extract data from standardized response format
      const data = responseData.data || responseData;
      
      return {
        authUrl: data.auth_url || data.authUrl || data.authorization_url,
        state: data.state,
        codeVerifier: data.code_verifier || data.codeVerifier, // For X OAuth flow
      };
    } catch (error) {
      console.error(`❌ [INTEGRATIONS] Error getting auth URL for ${platform}:`, error);
      throw error;
    }
  }

  /**
   * Handle X callback manually for mobile apps
   */
  async handleXCallback(authorizationCode: string, state: string): Promise<any> {
    try {
      const fullUrl = `${INTEGRATIONS_CONFIG.BASE_URL}/x/callback`;
            
      const params = new URLSearchParams({
        code: authorizationCode,
        state: state
      });
      
      const response = await fetch(`${fullUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'APS-API-Key': INTEGRATIONS_CONFIG.API_KEY,
        },
      });

            
      if (!response.ok) {
        const errorText = await response.text();
                throw new Error(`X callback failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
            return data;
    } catch (error) {
            throw error;
    }
  }

  /**
   * Exchange authorization code for tokens (X only)
   */
  async exchangeToken(userId: string, provider: 'x', codeVerifier: string, authorizationCode: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    if (provider !== 'x') {
      throw new Error('Token exchange is only required for X platform');
    }

    try {
      const fullUrl = `${INTEGRATIONS_CONFIG.BASE_URL}/exchange-token`;
      
      const requestBody = {
        user_id: userId,
        provider: provider,
        code_verifier: codeVerifier,
        authorization_code: authorizationCode,
      };

      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify(requestBody),
      });

      
      if (!response.ok) {
        const errorText = await response.text();
                
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          // Handle standardized API error format
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        
        throw new Error(errorData.error || 'Failed to exchange token');
      }

      const responseText = await response.text();
            
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
                throw new Error('Invalid JSON response from integrations service');
      }

      // Check API success status
      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to exchange token');
      }

            
    } catch (error) {
      console.error(`❌ [INTEGRATIONS] Error exchanging token:`, error);
      throw error;
    }
  }

  /**
   * Get auth records for one user (to check connection status)
   */
  async getAuthRecords(userId: string): Promise<AuthRecord[]> {
    if (!this.isAvailable()) {
            return [];
    }

    try {
      const fullUrl = `${INTEGRATIONS_CONFIG.BASE_URL}/auth?user_id=${encodeURIComponent(userId)}`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

            
      if (!response.ok) {
        const errorText = await response.text();
                
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          // Handle standardized API error format
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        
        throw new Error(errorData.error || 'Failed to get auth records');
      }

      const responseText = await response.text();
            
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
                throw new Error('Invalid JSON response from integrations service');
      }

      // Check API success status  
      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get auth records');
      }

      return responseData.data || responseData;
    } catch (error) {
      console.error('❌ [INTEGRATIONS] Error getting auth records:', error);
      throw error;
    }
  }

  /**
   * Resolve the X post character limit for a user's connected account (280 basic /
   * 25000 for X Premium) so the tweet composer can show the right limit up front.
   * Fail-safe: any error resolves to the basic 280 limit.
   */
  async getXCharacterLimit(userId: string): Promise<{ character_limit: number; is_premium: boolean; connected: boolean; subscription_type: string | null }> {
    const fallback = { character_limit: 280, is_premium: false, connected: false, subscription_type: null };
    if (!this.isAvailable()) return fallback;
    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/character-limit/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });
      if (!response.ok) return fallback;
      const data = await response.json();
      const info = data?.data || data;
      return {
        character_limit: typeof info?.character_limit === 'number' ? info.character_limit : 280,
        is_premium: !!info?.is_premium,
        connected: !!info?.connected,
        subscription_type: info?.subscription_type ?? null,
      };
    } catch (error) {
      console.error('Error getting X character limit:', error);
      return fallback;
    }
  }

  /**
   * Check connection status for an athlete across all platforms
   */
  async getConnectionStatus(athleteId: string): Promise<ConnectionStatus> {
    if (!this.isAvailable()) {
            return {
        youtube: false,
        twitter: false,
        gmail: false,
      };
    }

    try {
      const authRecords = await this.getAuthRecords(athleteId);
            
      const athleteRecords = authRecords.filter(record => record.user_id === athleteId);
            
      const connectionStatus = {
        youtube: athleteRecords.some(record => record.provider === 'youtube'),
        twitter: athleteRecords.some(record => record.provider === 'x'), // Fixed to match API spec
        gmail: athleteRecords.some(record => record.provider === 'gmail'),
      };
      
                  
      return connectionStatus;
    } catch (error) {
      console.error('Error checking connection status:', error);
      // Return false for all platforms if there's an error
      return {
        youtube: false,
        twitter: false,
        gmail: false,
      };
    }
  }

  /**
   * Post a video to YouTube
   */
  async postToYouTube(athleteId: string, videoData: {
    title: string;
    description?: string;
    video_urls: string[];
    options?: {
      privacyStatus?: 'private' | 'unlisted' | 'public';
      tags?: string[];
    };
  }) {
    if (!this.isAvailable()) {
      throw new Error('YouTube integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/youtube`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          ...videoData,
          user_id: athleteId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to post to YouTube');
      }

      return await response.json();
    } catch (error) {
      console.error('Error posting to YouTube:', error);
      throw error;
    }
  }

  /**
   * Get YouTube channel information
   */
  async getYouTubeChannel(athleteId: string) {
    if (!this.isAvailable()) {
      throw new Error('YouTube integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/youtube/channel/${athleteId}`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to get YouTube channel info');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from YouTube channel API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get YouTube channel info');
      }

      return responseData;
    } catch (error) {
      console.error('Error getting YouTube channel info:', error);
      throw error;
    }
  }

  /**
   * Post a tweet to X
   */
  async postToTwitter(athleteId: string, tweetData: {
    text: string;
    media_urls?: string[];
    mentions?: {
      coach_ids?: string[];
      school_ids?: string[];
    };
  }) {
    if (!this.isAvailable()) {
      throw new Error('X integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          ...tweetData,
          user_id: athleteId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to post to X';
        try {
          const errorData = JSON.parse(errorText);
          // Prefer the curated `error` copy over raw `details` (X's own text).
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch (e) {
          // Not JSON
        }
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from X API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.details || responseData.error || 'Failed to post to X');
      }

      return responseData.data || responseData;
    } catch (error) {
      console.error('Error posting to X:', error);
      throw error;
    }
  }

  /**
   * Send email via Gmail
   */
  async sendGmail(athleteId: string, emailData: {
    recipients: Array<{
      email: string;
      coach_id?: string;
      school_id?: string;
    }>;
    subject: string;
    body: string;
    reply_to?: string;
    display_name?: string;
    cc_emails?: string[];
    bcc_emails?: string[];
    attachments?: {
      filename: string;
      url: string; // S3 URL
      content_type: string; // MIME type like 'application/pdf', 'image/png', etc.
      size?: number; // Optional file size in bytes
    }[];
  }) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/send`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          ...emailData,
          user_id: athleteId,
        }),
      });

      const result = await response.json().catch(() => ({}));
      const failures = Array.isArray(result?.data?.failures) ? result.data.failures : [];
      const sentCount = Number(result?.data?.count ?? (Array.isArray(result?.data?.sent) ? result.data.sent.length : 0));
      // A 200 can still carry failures. When nothing went out, it is a failure.
      if (!response.ok || result?.success === false || (failures.length > 0 && sentCount === 0)) {
        throw new GmailSendError(
          result?.error || failures[0]?.error || 'Failed to send email',
          result?.code || failures[0]?.code || null,
          result?.reauth_reason || failures[0]?.reauth_reason || null,
        );
      }

      return result;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  /**
   * Hand the whole list to the server. It sends one email per coach and keeps
   * going if this page closes. The sender gets a notice when it is done.
   */
  async sendGmailBulk(userId: string, request: GmailBulkSendRequest): Promise<GmailBulkSendResult> {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/send-bulk`, {
      method: 'POST',
      headers: integrationsHeaders(),
      body: JSON.stringify({ ...request, user_id: userId }),
    });

    if (response.status === 413) {
      throw new GmailSendError('Request too large', 'payload_too_large', null, 413);
    }

    const result = await response.json().catch(() => ({}));
    if (!response.ok || result?.success === false || !result?.job_id) {
      throw new GmailSendError(
        result?.error || 'Failed to send email',
        result?.code || null,
        result?.reauth_reason || null,
        response.ok ? null : response.status,
      );
    }

    const total = Number(result.total ?? request.messages.length);
    const blocked = Number(result.free_limit_blocked ?? 0);
    return {
      job_id: String(result.job_id),
      total,
      queued: Number(result.queued ?? total - blocked),
      free_limit_blocked: blocked,
    };
  }

  /**
   * Get sent emails via Gmail
   */
  async getSentGmails(athleteId: string, options?: {
    limit?: number;
  }) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const limit = options?.limit || 20;
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/sent/${athleteId}?limit=${limit}`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to get sent emails');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get sent emails');
      }

      return responseData;
    } catch (error) {
      console.error('Error getting sent emails:', error);
      throw error;
    }
  }

  /**
   * Get unread Gmail replies for an athlete
   */
  async getUnreadGmailReplies(athleteId: string) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/replies/${athleteId}/unread`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to get unread replies');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get unread replies');
      }

      return responseData;
    } catch (error) {
      console.error('Error getting unread replies:', error);
      throw error;
    }
  }

  /**
   * Get Gmail thread details
   */
  async getGmailThread(athleteId: string, threadId: string) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/thread/${athleteId}/${threadId}`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to get thread');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get thread');
      }

      return responseData;
    } catch (error) {
      console.error('Error getting thread:', error);
      throw error;
    }
  }

  /**
   * Get Gmail thread replies (contains complete thread conversation)
   */
  async getGmailThreadReplies(athleteId: string, threadId: string) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/thread/${athleteId}/${threadId}/replies`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to get thread replies');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to get thread replies');
      }

      return responseData;
    } catch (error) {
      console.error('Error getting thread replies:', error);
      throw error;
    }
  }

  /**
   * Send a reply to a Gmail thread
   */
  async sendThreadReply(athleteId: string, threadId: string, replyData: {
    recipients: string[];
    subject: string;
    body: string;
    cc_emails?: string[];
    bcc_emails?: string[];
    attachments?: { filename: string; url: string; content_type: string; size?: number; }[];
    display_name?: string;
  }) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/thread/${athleteId}/${threadId}/reply`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          recipients: replyData.recipients,
          subject: replyData.subject,
          body: replyData.body,
          cc_emails: replyData.cc_emails,
          bcc_emails: replyData.bcc_emails,
          attachments: replyData.attachments,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to send thread reply');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to send thread reply');
      }

      return responseData;
    } catch (error) {
      console.error('Error sending thread reply:', error);
      throw error;
    }
  }

  /**
   * Mark a Gmail message as read
   */
  async markMessageAsRead(athleteId: string, messageId: string) {
    if (!this.isAvailable()) {
      throw new Error('Gmail integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/message/${athleteId}/${messageId}/read`, {
        method: 'PUT',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          if (errorData.success === false && errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to mark message as read');
      }

      const responseText = await response.text();
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid JSON response from Gmail API');
      }

      if (responseData.success === false) {
        throw new Error(responseData.error || 'Failed to mark message as read');
      }

      return responseData;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  /**
   * Get unified analytics for an athlete
   */
  async getAnalytics(athleteId: string, options?: {
    limit?: number;
    provider?: 'youtube' | 'x';
  }) {
    if (!this.isAvailable()) {
      throw new Error('Analytics integration is not available');
    }

    try {
      const fullUrl = `${INTEGRATIONS_CONFIG.BASE_URL}/social/analytics/${athleteId}`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get analytics');
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('❌ [API] Error getting analytics:', error);
      throw error;
    }
  }

  /**
   * Disconnect X integration for a user
   */
  async disconnectX(userId: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/disconnect/${userId}`, {
        method: 'DELETE',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to disconnect X integration');
      }
    } catch (error) {
      console.error('Error disconnecting X integration:', error);
      throw error;
    }
  }

  /**
   * Disconnect YouTube integration for a user
   */
  async disconnectYouTube(userId: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/youtube/disconnect/${userId}`, {
        method: 'DELETE',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to disconnect YouTube integration');
      }
    } catch (error) {
      console.error('Error disconnecting YouTube integration:', error);
      throw error;
    }
  }

  /**
   * Disconnect Gmail integration for a user
   */
  async disconnectGmail(userId: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/gmail/disconnect/${userId}`, {
        method: 'DELETE',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to disconnect Gmail integration');
      }
    } catch (error) {
      console.error('Error disconnecting Gmail integration:', error);
      throw error;
    }
  }

  /**
   * Send an X Direct Message
   */
  async sendXDM(athleteId: string, dmData: {
    text: string;
    coach_id?: string;
    x_handle?: string;
    participant_id?: string;
    dm_conversation_id?: string;
  }): Promise<XDMSendResponse> {
    if (!this.isAvailable()) {
      throw new Error('X integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/dm/send`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          user_id: athleteId,
          ...dmData,
        }),
      });

      if (response.status === 429) {
        const errorData = await response.json();
        const resetAt = errorData.rate_limit_reset_at;
        const retryAfter = errorData.retry_after_seconds;
        const msg = retryAfter
          ? `Rate limited. Try again in ${retryAfter} seconds.`
          : `Rate limited. Try again after ${new Date(resetAt).toLocaleTimeString()}.`;
        throw new Error(msg);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send DM' }));
        throw new Error(errorData.error || 'Failed to send DM');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending X DM:', error);
      throw error;
    }
  }

  /**
   * Check if the user can send a DM to a specific X handle
   */
  async canSendDM(athleteId: string, xHandle: string): Promise<CanSendDMResponse> {
    if (!this.isAvailable()) {
      throw new Error('X integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/dm/can-send`, {
        method: 'POST',
        headers: integrationsHeaders(),
        body: JSON.stringify({
          user_id: athleteId,
          x_handle: xHandle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to check DM eligibility' }));
        throw new Error(errorData.error || 'Failed to check DM eligibility');
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking DM eligibility:', error);
      throw error;
    }
  }

  /**
   * Get X DM conversation history with a coach (DB only, fast)
   */
  async getXDMConversation(athleteId: string, coachId: string): Promise<XDMConversationResponse> {
    if (!this.isAvailable()) {
      throw new Error('X integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/dm/users/${athleteId}/conversations/${coachId}`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get DM conversation' }));
        throw new Error(errorData.error || 'Failed to get DM conversation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting X DM conversation:', error);
      throw error;
    }
  }

  /**
   * Get all X DM threads for an athlete
   */
  async getXDMThreads(athleteId: string): Promise<XDMThreadsResponse> {
    if (!this.isAvailable()) {
      throw new Error('X integration is not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/x/dm/users/${athleteId}/threads`, {
        method: 'GET',
        headers: integrationsHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get DM threads' }));
        throw new Error(errorData.error || 'Failed to get DM threads');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting X DM threads:', error);
      throw error;
    }
  }

  /**
   * Get latest tweets for a coach by coach ID
   */
  async getCoachTweets(coachId: number): Promise<CoachTweetsResponse> {
    if (!this.isAvailable()) {
      throw new Error('Social media integrations are not available');
    }

    try {
      const response = await fetch(`${INTEGRATIONS_CONFIG.BASE_URL}/coach-tweets/${coachId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'APS-API-Key': INTEGRATIONS_CONFIG.API_KEY,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to fetch coach tweets');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching coach tweets:', error);
      throw error;
    }
  }
}

export const integrationsAPI = new IntegrationsAPI();
